import { useMemo, useState } from 'react';
import { Chip, MONO } from '../composants/base.jsx';
import { FicheJoueur } from '../composants/FicheJoueur.jsx';
import { LIGNE, LIGNES, LIBELLE, STATUTS } from '../data/postes.js';

const TON = { bon: 'var(--acc2)', attention: 'var(--warn)', mauvais: 'var(--bad)' };

/** L'écran de consultation. On y vient au calme, jamais dans l'urgence d'un match. */
export function EcranEffectif({ instantane, fraicheur }) {
  const [filtre, setFiltre] = useState('Tous');
  const [ouvert, setOuvert] = useState(null);
  const [voirPartis, setVoirPartis] = useState(false);

  const effectif = instantane?.effectif ?? [];
  const partis = instantane?.horsGroupe ?? [];

  const liste = useMemo(() => {
    const dedans = filtre === 'Tous' ? effectif : effectif.filter((j) => LIGNE[j.poste] === filtre);
    // Les disponibles d'abord : c'est la seule chose qu'on cherche vraiment ici.
    return [...dedans].sort((a, b) => {
      const d = rang(a) - rang(b);
      return d || (a.numero ?? 99) - (b.numero ?? 99);
    });
  }, [effectif, filtre]);

  const dispos = effectif.filter((j) => j.statut === 'ok').length;

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <header style={{ flex: 'none', padding: 'calc(env(safe-area-inset-top) + 12px) 18px 12px' }}>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, letterSpacing: '-.5px' }}>Effectif</h1>
        <div style={{ fontSize: 11, fontFamily: MONO, color: 'var(--ink2)', marginTop: 4 }}>
          {effectif.length} joueurs · {dispos} disponibles · {fraicheur}
        </div>
      </header>

      <div className="defileX" style={{ flex: 'none', display: 'flex', gap: 7, padding: '0 18px 12px' }}>
        {['Tous', ...LIGNES].map((l) => (
          <Chip key={l} actif={filtre === l} onClick={() => setFiltre(l)} style={{ fontFamily: 'var(--police)', fontSize: 12.5 }}>{l}</Chip>
        ))}
      </div>

      <div className="defile" style={{ flex: 1, minHeight: 0, padding: '0 14px 20px' }}>
        {liste.map((j) => {
          const st = STATUTS[j.statut] ?? STATUTS.ok;
          return (
            <button
              key={j.id}
              onClick={() => setOuvert(j)}
              style={{
                display: 'flex', alignItems: 'center', gap: 11, width: '100%', textAlign: 'left',
                padding: 10, marginBottom: 6, borderRadius: 14,
                background: 'var(--surf)', border: '1px solid var(--line)',
                opacity: j.statut === 'blesse' || j.statut === 'susp' ? .6 : 1,
              }}
            >
              <span style={{ width: 38, height: 38, flex: 'none', borderRadius: 11, background: 'var(--surf2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: MONO, fontWeight: 700, fontSize: 14 }}>
                {j.numero ?? '·'}
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 14, fontWeight: 600 }}>{j.nom}</span>
                  {j.capitaine && <span style={{ fontSize: 9, fontWeight: 800, color: 'var(--bg)', background: 'var(--ink)', borderRadius: 4, padding: '1px 4px' }}>C</span>}
                </span>
                <span style={{ display: 'block', fontSize: 10.5, fontFamily: MONO, color: 'var(--ink2)', marginTop: 2 }}>
                  {LIBELLE[j.poste] ?? j.poste}{j.natNom ? ` · ${j.natNom}` : ''}{j.age ? ` · ${j.age} ans` : ''}
                </span>
                {/* La polyvalence se lit d'un coup d'œil : c'est elle qui décide qui
                    l'application proposera quand on touchera un poste vide. */}
                {j.alt?.length > 0 && (
                  <span style={{ display: 'flex', gap: 4, marginTop: 5 }}>
                    {j.alt.slice(0, 3).map((a) => (
                      <span key={a} style={{ fontFamily: MONO, fontSize: 9, fontWeight: 700, color: 'var(--acc2)', border: '1px solid var(--line)', borderRadius: 5, padding: '1px 5px' }}>{a}</span>
                    ))}
                  </span>
                )}
              </span>
              {j.statut !== 'ok' && (
                <span style={{ flex: 'none', padding: '4px 8px', borderRadius: 7, border: `1px solid ${TON[st.ton]}`, color: TON[st.ton], fontSize: 10, fontWeight: 700 }}>{st.libelle}</span>
              )}
            </button>
          );
        })}
        {/* LES PARTIS, REPLIÉS.
            Ils sont hors de l'effectif partout ailleurs — sélecteur, banc,
            pré-remplissage — parce qu'un prêté proposé en plein match est une faute.
            Mais ils restent visibles ici : si une ligne du relevé est fausse, on doit
            pouvoir s'en apercevoir. Un joueur disparu sans explication serait pire
            qu'un joueur mal rangé. */}
        {partis.length > 0 && filtre === 'Tous' && (
          <div style={{ marginTop: 14 }}>
            <button
              onClick={() => setVoirPartis((v) => !v)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '11px 4px' }}
            >
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.9px', color: 'var(--ink2)' }}>
                HORS GROUPE · {partis.length}
              </span>
              <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--ink3)' }}>{voirPartis ? 'Masquer' : 'Voir'}</span>
            </button>
            {voirPartis && partis.map((p) => (
              <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: 10, marginBottom: 6, borderRadius: 14, background: 'var(--surf)', border: '1px dashed var(--line)', opacity: .7 }}>
                <span style={{ width: 38, height: 38, flex: 'none', borderRadius: 11, background: 'var(--surf2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: MONO, fontWeight: 700, fontSize: 14, color: 'var(--ink3)' }}>
                  {p.numero ?? '·'}
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 14, fontWeight: 600 }}>{(p.prenom ? p.prenom + ' ' : '') + (p.patronyme ?? p.nom)}</span>
                  <span style={{ display: 'block', fontSize: 10.5, fontFamily: MONO, color: 'var(--ink2)', marginTop: 2 }}>{p.raison}</span>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <FicheJoueur ouverte={!!ouvert} joueur={ouvert} surLeTerrain={false} onFermer={() => setOuvert(null)} />
    </div>
  );
}

const rang = (j) => (j.statut === 'ok' ? 0 : j.statut === 'doute' ? 1 : 2);
